import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useAuthErrorHandler } from '../../utils/authUtils';
import * as adminService from '../../services/adminService';
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Paper,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Stack,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LockResetIcon from '@mui/icons-material/LockReset';
import GroupIcon from '@mui/icons-material/Group';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import ShieldIcon from '@mui/icons-material/Shield';
import VisibilityIcon from '@mui/icons-material/Visibility';

const USER_STATUSES = ['all', 'pending_approval', 'approved', 'rejected'];

const STATUS_LABEL: Record<string, string> = {
  all: 'All',
  pending_approval: 'Pending approval',
  approved: 'Approved',
  rejected: 'Rejected',
};

const STATUS_TONE: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  approved: 'success',
  pending_approval: 'warning',
  rejected: 'error',
};

const formatDateTime = (input: string | null | undefined): string => {
  if (!input) return 'Never';
  const d = new Date(input);
  if (isNaN(d.getTime())) return 'Never';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const UserManagementPage: React.FC = () => {
  const { token, user: loggedInUser } = useAuth();
  const { handleAuthError } = useAuthErrorHandler();
  const [users, setUsers] = useState<adminService.User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [resetLinkDialogOpen, setResetLinkDialogOpen] = useState(false);
  const [resetLink, setResetLink] = useState('');
  const [resetLinkCopied, setResetLinkCopied] = useState(false);
  const [savingUserIds, setSavingUserIds] = useState<Set<number>>(new Set());

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setActionError(null);
    setActionSuccess(null);
    try {
      if (!token) {
        setError('Authentication token is not available. Please log in again.');
        setIsLoading(false);
        setUsers([]);
        return;
      }
      const fetchedUsers = await adminService.getUsers(token);
      setUsers(fetchedUsers);
    } catch (err: unknown) {
      if (!handleAuthError(err)) {
        setError(err instanceof Error ? err.message : 'Failed to fetch users.');
      }
    }
    setIsLoading(false);
  }, [token, handleAuthError]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(
    () =>
      statusFilter === 'all'
        ? users
        : users.filter((u) => u.approval_status === statusFilter),
    [users, statusFilter],
  );

  const stats = useMemo(() => {
    const total = users.length;
    const pending = users.filter((u) => u.approval_status === 'pending_approval').length;
    const admins = users.filter((u) => u.is_admin).length;
    const readOnly = users.filter((u) => u.is_read_only).length;
    return [
      { label: 'Total Users', value: total.toLocaleString(), icon: <GroupIcon /> },
      {
        label: 'Pending Approval',
        value: pending.toLocaleString(),
        icon: <PendingActionsIcon />,
        tone: pending > 0 ? ('warning' as const) : undefined,
      },
      { label: 'Admins', value: admins.toLocaleString(), icon: <ShieldIcon /> },
      { label: 'Read-Only', value: readOnly.toLocaleString(), icon: <VisibilityIcon /> },
    ];
  }, [users]);

  const markUserSaving = (userId: number, saving: boolean): void => {
    setSavingUserIds((prev) => {
      const next = new Set(prev);
      if (saving) next.add(userId);
      else next.delete(userId);
      return next;
    });
  };

  const handleStatusChange = async (
    userId: number,
    newStatus: adminService.User['approval_status'],
  ) => {
    setActionError(null);
    setActionSuccess(null);
    if (!token) {
      setActionError('Authentication token is not available.');
      return;
    }
    markUserSaving(userId, true);
    try {
      const response = await adminService.updateUserStatus(userId, newStatus, token);
      setActionSuccess(response.message || 'User status updated.');
      setUsers((prev) =>
        prev.map((u) => (u.user_id === userId ? { ...u, approval_status: newStatus } : u)),
      );
    } catch (err: unknown) {
      if (!handleAuthError(err)) {
        setActionError(err instanceof Error ? err.message : 'Failed to update user status.');
      }
    } finally {
      markUserSaving(userId, false);
    }
  };

  const handleAdminRoleChange = async (userId: number, isAdmin: boolean) => {
    setActionError(null);
    setActionSuccess(null);
    if (!token) {
      setActionError('Authentication token is not available.');
      return;
    }
    markUserSaving(userId, true);
    try {
      const response = await adminService.updateUserAdminRole(userId, isAdmin, token);
      setActionSuccess(response.message || 'User admin role updated.');
      setUsers((prev) =>
        prev.map((u) => (u.user_id === userId ? { ...u, is_admin: isAdmin } : u)),
      );
    } catch (err: unknown) {
      if (!handleAuthError(err)) {
        setActionError(err instanceof Error ? err.message : 'Failed to update admin role.');
      }
    } finally {
      markUserSaving(userId, false);
    }
  };

  const handleReadOnlyRoleChange = async (userId: number, isReadOnly: boolean) => {
    setActionError(null);
    setActionSuccess(null);
    if (!token) {
      setActionError('Authentication token is not available.');
      return;
    }
    markUserSaving(userId, true);
    try {
      const response = await adminService.updateUserReadOnlyRole(userId, isReadOnly, token);
      setActionSuccess(response.message || 'Read-only access updated.');
      setUsers((prev) =>
        prev.map((u) => (u.user_id === userId ? { ...u, is_read_only: isReadOnly } : u)),
      );
    } catch (err: unknown) {
      if (!handleAuthError(err)) {
        setActionError(err instanceof Error ? err.message : 'Failed to update read-only access.');
      }
    } finally {
      markUserSaving(userId, false);
    }
  };

  const handleGenerateResetLink = async (userId: number) => {
    setActionError(null);
    setActionSuccess(null);
    if (!token) {
      setActionError('Authentication token is not available.');
      return;
    }
    try {
      const response = await adminService.generateResetLink(userId, token);
      setResetLink(response.resetLink);
      setResetLinkCopied(false);
      setResetLinkDialogOpen(true);
    } catch (err: unknown) {
      if (!handleAuthError(err)) {
        setActionError(err instanceof Error ? err.message : 'Failed to generate reset link.');
      }
    }
  };

  const handleCopyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLink);
      setResetLinkCopied(true);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = resetLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setResetLinkCopied(true);
    }
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 3,
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
        }}
      >
        <Typography variant="h4" component="h1">
          User Management
        </Typography>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Filter by Status</InputLabel>
          <Select
            value={statusFilter}
            label="Filter by Status"
            onChange={(e) => setStatusFilter(e.target.value as string)}
          >
            {USER_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {STATUS_LABEL[status] ?? status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat) => (
          <Grid key={stat.label} item xs={6} md={3}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ letterSpacing: '0.04em', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {stat.label}
                  </Typography>
                  <Box
                    sx={{
                      color: stat.tone === 'warning' ? 'warning.main' : 'text.disabled',
                      display: 'flex',
                      alignItems: 'center',
                      '& svg': { fontSize: 18 },
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}
                >
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {actionError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}
      {actionSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : filteredUsers.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No users found for this filter.</Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 0, overflow: 'hidden' }}>
          <TableContainer>
            <Table aria-label="user management table">
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Registered</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Admin</TableCell>
                  <TableCell align="center">Read-Only</TableCell>
                  <TableCell align="right">{''}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => {
                  const isSelf = Number(user.user_id) === Number(loggedInUser?.id);
                  const isSaving = savingUserIds.has(user.user_id);
                  return (
                    <TableRow key={user.user_id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {user.username}
                        {isSelf && (
                          <Chip
                            label="You"
                            size="small"
                            variant="outlined"
                            sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{user.email}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {formatDateTime(user.created_at)}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                        {formatDateTime(user.last_login)}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABEL[user.approval_status] || user.approval_status}
                          color={STATUS_TONE[user.approval_status] || 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={Boolean(user.is_admin)}
                          onChange={(e) => handleAdminRoleChange(user.user_id, e.target.checked)}
                          disabled={(isSelf && Boolean(user.is_admin)) || isSaving}
                          inputProps={{
                            'aria-label': `Toggle admin access for ${user.username}`,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Switch
                          size="small"
                          checked={Boolean(user.is_read_only)}
                          onChange={(e) =>
                            handleReadOnlyRoleChange(user.user_id, e.target.checked)
                          }
                          disabled={isSaving}
                          color="warning"
                          inputProps={{
                            'aria-label': `Toggle read-only access for ${user.username}`,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" sx={{ flexWrap: 'nowrap' }}>
                          {user.approval_status === 'pending_approval' && (
                            <>
                              <Button
                                variant="contained"
                                color="success"
                                size="small"
                                disabled={isSaving}
                                onClick={() => handleStatusChange(user.user_id, 'approved')}
                              >
                                Approve
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                size="small"
                                disabled={isSaving}
                                onClick={() => handleStatusChange(user.user_id, 'rejected')}
                              >
                                Reject
                              </Button>
                            </>
                          )}
                          {user.approval_status === 'approved' && (
                            <>
                              <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                disabled={isSaving}
                                onClick={() => handleStatusChange(user.user_id, 'rejected')}
                              >
                                Revoke
                              </Button>
                              <Button
                                variant="outlined"
                                color="inherit"
                                size="small"
                                disabled={isSaving}
                                onClick={() =>
                                  handleStatusChange(user.user_id, 'pending_approval')
                                }
                              >
                                Pending
                              </Button>
                            </>
                          )}
                          {user.approval_status === 'rejected' && (
                            <Button
                              variant="outlined"
                              color="success"
                              size="small"
                              disabled={isSaving}
                              onClick={() => handleStatusChange(user.user_id, 'approved')}
                            >
                              Re-approve
                            </Button>
                          )}
                          <Tooltip title="Generate password reset link">
                            <span>
                              <IconButton
                                size="small"
                                disabled={isSaving}
                                onClick={() => handleGenerateResetLink(user.user_id)}
                                sx={{
                                  color: 'text.secondary',
                                  '&:hover, &.Mui-focusVisible': { color: 'primary.main' },
                                }}
                              >
                                <LockResetIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={resetLinkDialogOpen}
        onClose={() => setResetLinkDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Password Reset Link</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            size="small"
            value={resetLink}
            InputProps={{ readOnly: true }}
            sx={{ mt: 1 }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This link expires in 1 hour.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setResetLinkDialogOpen(false)}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopyResetLink}
            color={resetLinkCopied ? 'success' : 'primary'}
          >
            {resetLinkCopied ? 'Copied' : 'Copy'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
